import type {
  ChatResultStats,
  FlightCardData,
  NlpFilters,
  NlpFlightCardData,
  StructuredMultiSearchRequest,
} from '@massilia/queries'

import {
  formatDateNatural,
  getApiErrorMessage,
  getRateLimitScope,
  isExpiredSearchSessionError,
  isProviderUnavailableError,
  useCreateBookingSession,
  useDebugAccessQuery,
  useMultiFlightSearch,
  useSearchFromQuery,
} from '@massilia/queries'
import { Label, Switch, useIsMobile } from '@massilia/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

import type { DurationRange } from '@/components/flights/filters/duration-filter'
import type { PriceRange } from '@/components/flights/filters/price-filter'

import { toPricePreview } from '@/components/booking/price-preview'
import { SessionExpiredError } from '@/components/booking/session-expired-error'
import { DebugProvider } from '@/components/flights/debug-context'
import { FilteredEmptyCard } from '@/components/flights/filtered-empty-card'
import {
  applyFilters,
  FiltersSkeleton,
  FiltersStateProvider,
  FlightFilters,
  type FlightFiltersState,
  getPriceBounds,
  parseFilterTimeRanges,
  RESET_FILTERS_PATCH,
  urlParamsToNlpFilters,
} from '@/components/flights/filters'
import { FiltersButton } from '@/components/flights/filters/filters-button'
import { FiltersModal } from '@/components/flights/filters/filters-modal'
import { FlightCard } from '@/components/flights/flight-card'
import { FlightCardSkeleton } from '@/components/flights/flight-card-skeleton'
import { FlightErrorCard } from '@/components/flights/flight-error-card'
import { FlightSearchLoadingModal } from '@/components/flights/flight-search-loading-modal'
import { MultiFlightSearchForm } from '@/components/flights/multi-flight-search-form'
import { NoResultsCard } from '@/components/flights/no-results-card'
import { ResultsBackHome } from '@/components/flights/results-back-home'
import { BackToTopButton } from '@/components/ui/back-to-top-button'
import { luggageFilterSchema, stopsFilterSchema } from '@/lib/schemas/flight-results-search.schema'
import { useAuthStore } from '@/stores/auth.store'
import { useBookingPricePreviewStore } from '@/stores/booking-price-preview.store'
import { useChatWidgetStore } from '@/stores/chat-widget.store'
import { useFlightSearchStore } from '@/stores/flight-search.store'
import { useFilters } from '@/utils/use-search-params'

interface PassengerCount {
  adults: number
  babies: number
  infants: number
  seniors: number
  youngsters: number
}

type TripType = 'multi-city' | 'one-way' | 'round-trip'

export const Route = createFileRoute('/_booking/vols-multi/resultats')({
  component: FlightsManyQueryResultsPage,
  validateSearch: z.object({
    filterAirlines: z.string().optional(),
    filterDurationMax: z.coerce.number().optional(),
    filterDurationMin: z.coerce.number().optional(),
    filterLuggage: luggageFilterSchema.optional().default('0'),
    filterPriceMax: z.coerce.number().optional(),
    filterPriceMin: z.coerce.number().optional(),
    filterStops: stopsFilterSchema.optional().default('all'),
    filterTimeRanges: z.string().optional(),
    q: z.string().optional(),
    search: z.unknown().optional(),
  }),
})

// NB: les codes proviennent désormais de la compagnie OPÉRANTE (cf. flight-transformer).
// Fallback sur le code IATA brut si absent de cette table.
const AIRLINE_NAMES: Record<string, string> = {
  AF: 'Air France',
  LH: 'Lufthansa',
  LX: 'Swiss',
  SN: 'Brussels Airlines',
  SS: 'Corsair',
  TX: 'Air Caraïbes',
  UU: 'Air Austral',
}

const SLOT_LABELS: Record<string, string> = {
  '0-6': '0h–6h',
  '12-18': '12h–18h',
  '18-24': '18h–24h',
  '6-12': '6h–12h',
}

function derivePassengersCount(flight: FlightCardData): PassengerCount {
  return flight.pricing.perPassenger.reduce<PassengerCount>(
    (acc, p) => {
      if (p.type === 'Infant') acc.babies += p.count
      else if (p.type === 'Child') acc.infants += p.count
      else acc.adults += p.count
      return acc
    },
    { adults: 0, babies: 0, infants: 0, seniors: 0, youngsters: 0 }
  )
}

function FlightsManyQueryResultsPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { filters: searchParams, setFilters } = useFilters(Route.id)
  const initialQuery = searchParams.q
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
  const [isSessionExpiredOnCreate, setIsSessionExpiredOnCreate] = useState(false)
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | undefined>()
  const [showDebug, setShowDebug] = useState(false)

  const structuredParams = useMemo((): null | StructuredMultiSearchRequest => {
    if (!searchParams.search || typeof searchParams.search !== 'object') return null
    return searchParams.search as StructuredMultiSearchRequest
  }, [searchParams.search])

  const isStructuredMode = structuredParams !== null

  const { isAuthenticated } = useAuthStore()
  const { setLastFlightResults } = useFlightSearchStore()
  const setPricePreview = useBookingPricePreviewStore((state) => state.setPricePreview)
  const { data: debugAccess } = useDebugAccessQuery(isAuthenticated)

  const nlpQuery = useSearchFromQuery(!isStructuredMode ? (initialQuery ?? null) : null)
  const structuredQuery = useMultiFlightSearch(structuredParams)

  const {
    data: results,
    error,
    isFetching,
    refetch,
  } = isStructuredMode ? structuredQuery : nlpQuery
  const isLoading = isFetching
  const { mutateAsync: createBookingSession } = useCreateBookingSession()
  const isFlightMaintenance = isProviderUnavailableError(error)
  const flightRateLimitScope = getRateLimitScope(error)

  const handleStructuredSubmit = useCallback(
    (params: StructuredMultiSearchRequest) => {
      setFilters({ search: params as unknown as string })
    },
    [setFilters]
  )

  const handleSelectFlight = useCallback(
    async (flight: FlightCardData, offerTransactionKey: string, selectedJourneyKeys: string[]) => {
      const origin = flight.outbound.departureCity
      const destination = flight.outbound.arrivalCity || ''
      const departureDate = flight.outbound.departureDate
      const returnDate = flight.return?.departureDate
      const tripType: TripType = flight.return ? 'round-trip' : 'one-way'

      setLastFlightResults(searchParams as Record<string, unknown>, '/vols-multi/resultats')

      try {
        const session = await createBookingSession({
          accountCode: flight.pricing.accountCode,
          departureDate,
          destination,
          journeyKeys: selectedJourneyKeys,
          offerKey: flight.offerKey,
          origin,
          passengersCount: derivePassengersCount(flight),
          returnDate,
          transactionKey: offerTransactionKey,
          tripType,
        })

        // Aperçu du tarif pour la page suivante : le price Affidium prend plusieurs
        // secondes, l'offre qu'on vient de cliquer porte déjà tout sauf les conditions.
        setPricePreview(session.sessionId, toPricePreview(flight, selectedJourneyKeys))

        void navigate({
          search: { session: session.sessionId },
          to: '/informations',
        })
      } catch (err) {
        if (isExpiredSearchSessionError(err)) {
          setSessionExpiredMessage(getApiErrorMessage(err))
          setIsSessionExpiredOnCreate(true)
          return
        }
        throw err
      }
    },
    [createBookingSession, navigate, searchParams, setLastFlightResults, setPricePreview]
  )

  // Base order: account-code offers first, then cheapest. Used as the default
  // ("Recommandé") ordering before the user picks an explicit sort.
  const sortedOffers = useMemo(() => {
    if (!results?.offers || results.offers.length === 0) return []
    return [...results.offers].sort((a, b) => {
      const aHasAccountCode = a.pricing.accountCode !== undefined
      const bHasAccountCode = b.pricing.accountCode !== undefined
      if (aHasAccountCode && !bHasAccountCode) return -1
      if (!aHasAccountCode && bHasAccountCode) return 1
      const priceA = parseFloat(a.pricing.total.amount || '0')
      const priceB = parseFloat(b.pricing.total.amount || '0')
      return priceA - priceB
    })
  }, [results?.offers])

  // Parse the sidebar filter state from the URL search params.
  // Keep this aligned with /vols/resultats: range filters are active only
  // when both URL bounds are present. A single stale bound from the chat or a
  // previous URL must not collapse the slider to [min, min] and hide results.
  const filterState = useMemo((): FlightFiltersState => {
    const priceRange: PriceRange | undefined =
      searchParams.filterPriceMin !== undefined && searchParams.filterPriceMax !== undefined
        ? { max: searchParams.filterPriceMax, min: searchParams.filterPriceMin }
        : undefined

    const durationRange: DurationRange | undefined =
      searchParams.filterDurationMin !== undefined && searchParams.filterDurationMax !== undefined
        ? { max: searchParams.filterDurationMax, min: searchParams.filterDurationMin }
        : undefined

    return {
      durationRange,
      excludedAirlines: searchParams.filterAirlines ? searchParams.filterAirlines.split(',') : [],
      luggage: searchParams.filterLuggage ?? '1',
      priceRange,
      stops: searchParams.filterStops ?? 'all',
      timeFilters: parseFilterTimeRanges(searchParams.filterTimeRanges),
    }
  }, [
    searchParams.filterAirlines,
    searchParams.filterDurationMax,
    searchParams.filterDurationMin,
    searchParams.filterLuggage,
    searchParams.filterPriceMax,
    searchParams.filterPriceMin,
    searchParams.filterStops,
    searchParams.filterTimeRanges,
  ])

  // applyFilters preserves the underlying NLP objects, so the cast back to
  // NlpFlightCardData[] keeps nlpTransactionKey/offerId available.
  // Cap to 3 with-luggage + 3 without-luggage (in default recommended order).
  const visibleOffers = useMemo(() => {
    const sorted = applyFilters(sortedOffers, filterState) as NlpFlightCardData[]
    const withBag: NlpFlightCardData[] = []
    const withoutBag: NlpFlightCardData[] = []
    for (const offer of sorted) {
      const hasBag = offer.minCheckedBags > 0
      if (hasBag && withBag.length < 3) withBag.push(offer)
      else if (!hasBag && withoutBag.length < 3) withoutBag.push(offer)
      if (withBag.length === 3 && withoutBag.length === 3) break
    }
    return [...withBag, ...withoutBag]
  }, [sortedOffers, filterState])

  const appliedFilterBadges = useMemo(() => getFilterBadges(results?.appliedFilters), [results])

  const activeFiltersCount = useMemo(() => {
    if (!results?.offers) return 0
    let count = 0
    if (filterState.excludedAirlines.length > 0) count++
    if (filterState.stops !== 'all') count++
    if (filterState.luggage !== '1') count++
    if (filterState.priceRange) count++
    if (filterState.durationRange) count++
    if (filterState.timeFilters.length > 0) count++
    return count
  }, [filterState, results?.offers])

  // Publish page context to the chat widget so the bot can apply filters
  // (compagnie / prix / escales / etc.) directly from the conversation with
  // proper whitelist semantics and infeasibility warnings.
  const setPageContext = useChatWidgetStore((state) => state.setPageContext)
  const resultStats = useMemo<ChatResultStats | undefined>(() => {
    // Use the FULL server result set (results.offers), not visibleOffers —
    // visibleOffers is capped to 3 + 3 after sorting/filtering, so the chat
    // would only see the airlines/prices of the top picks. The classifier
    // and the LLM need the complete picture to:
    //   - match airline names not in the visible slice ("uniquement Corsair"
    //     when SS isn't among the top 6),
    //   - decide whether a user-requested price ceiling is feasible.
    const fullList = results?.offers ?? []
    if (!fullList.length) return undefined
    const seen = new Map<string, string>()
    for (const o of fullList) {
      if (!seen.has(o.airlineCode))
        seen.set(o.airlineCode, o.airline ?? AIRLINE_NAMES[o.airlineCode] ?? o.airlineCode)
    }
    const bounds = getPriceBounds(fullList)
    return {
      availableAirlines: Array.from(seen, ([code, name]) => ({ name, code })),
      offerCount: fullList.length,
      priceRange: { max: bounds.max, min: bounds.min },
    }
  }, [results?.offers])

  const currentFilters = useMemo<NlpFilters>(
    () =>
      urlParamsToNlpFilters({
        filterAirlines: searchParams.filterAirlines,
        filterDurationMax: searchParams.filterDurationMax,
        filterDurationMin: searchParams.filterDurationMin,
        filterLuggage: searchParams.filterLuggage,
        filterPriceMax: searchParams.filterPriceMax,
        filterPriceMin: searchParams.filterPriceMin,
        filterStops: searchParams.filterStops,
        filterTimeRanges: searchParams.filterTimeRanges,
      }),
    [
      searchParams.filterAirlines,
      searchParams.filterDurationMax,
      searchParams.filterDurationMin,
      searchParams.filterLuggage,
      searchParams.filterPriceMax,
      searchParams.filterPriceMin,
      searchParams.filterStops,
      searchParams.filterTimeRanges,
    ]
  )

  // Publish on every change; do NOT clear in cleanup — when deps change React
  // would briefly set pageContext to undefined, racing with sendMessage. The
  // chat-widget clears the context when the user leaves /vols-multi/resultats.
  useEffect(() => {
    setPageContext({ currentFilters, resultStats, route: 'flight_results' })
  }, [currentFilters, resultStats, setPageContext])

  return (
    <DebugProvider value={showDebug}>
      <FiltersStateProvider routeId={Route.id}>
        <div className="min-h-screen w-screen bg-blue-50">
          <div className="flex-1 border-b border-gray-200 bg-white py-4 shadow-sm">
            <div className="mb-3 px-4 md:px-20">
              <ResultsBackHome />
            </div>
            <div className="flex flex-col gap-6 px-4 md:px-20">
              {debugAccess?.allowed && (
                <div className="hidden flex-row items-center gap-2 md:flex">
                  <Label>Debug</Label>
                  <Switch
                    className={`text-xs hover:cursor-pointer ${showDebug ? 'font-semibold text-orange-500' : 'text-gray-400'}`}
                    onCheckedChange={(checked) => {
                      setShowDebug(checked)
                    }}
                  >
                    Debug
                  </Switch>
                </div>
              )}
              {structuredParams ? (
                <MultiFlightSearchForm
                  isLoading={isLoading}
                  onSubmit={handleStructuredSubmit}
                  searchParams={structuredParams}
                />
              ) : (
                <div className="flex w-full flex-1 flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Rechercher en langage naturel
                  </label>
                  <textarea
                    className="w-full rounded-md border border-gray-300 p-3 text-sm"
                    onChange={(e) => {
                      setFilters({ q: e.target.value })
                    }}
                    placeholder="Ex: Je veux un vol Paris → La Réunion entre le 10 et le 15 avril et retour entre le 20 et le 25 avril, 2 adultes, eco, bagage."
                    rows={3}
                    value={initialQuery ?? ''}
                  />
                  <div className="flex justify-end">
                    <button
                      className="rounded-md bg-[#1D95AD] px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isLoading || !initialQuery?.trim()}
                      onClick={() => {
                        setFilters({ q: initialQuery?.trim() })
                      }}
                      type="button"
                    >
                      Lancer la recherche
                    </button>
                  </div>
                  {!initialQuery?.trim() && !isLoading && !results && !error && (
                    <p className="text-xs text-gray-500">
                      Aucune requête fournie via l&apos;URL. Saisissez une recherche ou lancez-la
                      depuis le chat.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {isSessionExpiredOnCreate ? (
            <div className="flex flex-1 items-center justify-center px-4 py-8 md:px-20 md:py-12">
              <SessionExpiredError
                message={sessionExpiredMessage}
                onAction={() => {
                  void navigate({ to: '/recherche' })
                }}
              />
            </div>
          ) : (
            <>
              {results?.offers.length === 0 && (
                <div className="flex flex-1 items-center justify-center px-4 py-8 md:px-20 md:py-12">
                  <NoResultsCard
                    departureLabel="Départ"
                    destinationLabel="Destination"
                    onRetryWithoutLuggage={() => {
                      void refetch()
                    }}
                    withLuggage={false}
                  />
                </div>
              )}

              {error && (
                <div className="flex flex-1 items-center justify-center px-4 py-8 md:px-20 md:py-12">
                  <FlightErrorCard
                    onRetry={() => {
                      void refetch()
                    }}
                    type={
                      isFlightMaintenance
                        ? 'maintenance'
                        : flightRateLimitScope === 'day'
                          ? 'rate-limited-day'
                          : flightRateLimitScope === 'minute'
                            ? 'rate-limited-minute'
                            : 'error'
                    }
                  />
                </div>
              )}

              {(isLoading || (results && results.offers.length > 0)) && (
                <div className="w-full flex-1 gap-4 px-4 py-4 md:grid md:grid-cols-[1fr_3fr] md:gap-6 md:px-20 md:py-8">
                  {/* Filters sidebar - Desktop only */}
                  {!isMobile && (
                    <aside className="h-fit min-w-0">
                      {results && results.offers.length > 0 && (
                        <FlightFilters offers={results.offers} />
                      )}
                      {isLoading && <FiltersSkeleton />}
                    </aside>
                  )}

                  <main className="min-w-0">
                    <FlightSearchLoadingModal
                      destinationIataCode={structuredParams?.flights.at(-1)?.destination.iataCode}
                      onCancel={() => {
                        void navigate({ to: '/recherche' })
                      }}
                      open={isLoading}
                      originIataCode={structuredParams?.flights[0]?.departure.iataCode}
                    />

                    {isLoading && (
                      <div className="w-full space-y-4">
                        <div className="mb-4 h-5 w-32 animate-pulse rounded bg-gray-200" />
                        {Array.from({ length: 4 }).map((_, i) => (
                          <FlightCardSkeleton key={i} />
                        ))}
                      </div>
                    )}

                    {results && results.offers.length > 0 && (
                      <>
                        {/* Mobile: Filters Button */}
                        {isMobile && (
                          <FiltersButton
                            activeFiltersCount={activeFiltersCount}
                            onClick={() => {
                              setIsFiltersModalOpen(true)
                            }}
                          />
                        )}

                        <div className="mb-4 max-w-3xl text-sm text-gray-600">
                          <p>
                            Voici les{' '}
                            <strong className="font-semibold text-gray-900">
                              {visibleOffers.length} meilleures offres
                            </strong>{' '}
                            que nous avons sélectionnées pour vous{' '}
                            {renderPeriodSentence(structuredParams)}
                          </p>
                          <p className="mt-1">
                            Si vous souhaitez davantage d&apos;options vous pouvez renouveler votre
                            recherche{' '}
                            <a
                              className="font-bold text-sky-700 underline hover:text-sky-900"
                              href="https://www.massiliavoyages.fr/"
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              ICI
                            </a>
                            .
                          </p>
                        </div>

                        {appliedFilterBadges.length > 0 && (
                          <div className="mb-4 flex flex-wrap gap-2">
                            {appliedFilterBadges.map((badge) => (
                              <span
                                className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700"
                                key={badge}
                              >
                                {badge}
                              </span>
                            ))}
                          </div>
                        )}

                        {visibleOffers.length === 0 ? (
                          <FilteredEmptyCard
                            onReset={() => {
                              setFilters(RESET_FILTERS_PATCH)
                            }}
                          />
                        ) : (
                          <div className="space-y-4">
                            {visibleOffers.map((offer) => (
                            <div
                              className="overflow-hidden rounded-lg border border-sky-200 bg-white shadow-sm"
                              key={`${getSafeTransactionKey(offer, results.transactionKey)}__${offer.offerId}`}
                            >
                              <div className="border-b border-sky-200 bg-sky-50 px-4 py-3 text-base font-bold text-sky-900">
                                <span className="font-normal text-sky-700">Aller — </span>
                                <span className="font-extrabold">
                                  {formatDateNatural(offer.outbound.departureDate)}
                                </span>
                                {offer.return && (
                                  <>
                                    <span className="font-normal text-sky-700"> · Retour — </span>
                                    <span className="font-extrabold">
                                      {formatDateNatural(offer.return.departureDate)}
                                    </span>
                                  </>
                                )}
                              </div>
                              <FlightCard
                                className="rounded-none border-0 shadow-none hover:shadow-none"
                                flight={offer}
                                onSelect={(selectedFlight, selectedJourneyKeys) => {
                                  void handleSelectFlight(
                                    selectedFlight,
                                    getSafeTransactionKey(offer, results.transactionKey),
                                    selectedJourneyKeys
                                  )
                                }}
                                transactionKey={getSafeTransactionKey(
                                  offer,
                                  results.transactionKey
                                )}
                              />
                            </div>
                            ))}
                          </div>
                        )}

                        {/* Mobile: Filters Modal */}
                        {isMobile && (
                          <FiltersModal
                            isOpen={isFiltersModalOpen}
                            offers={results.offers}
                            onClose={() => {
                              setIsFiltersModalOpen(false)
                            }}
                          />
                        )}
                      </>
                    )}
                  </main>
                </div>
              )}
            </>
          )}

          <BackToTopButton />
        </div>
      </FiltersStateProvider>
    </DebugProvider>
  )
}

function getFilterBadges(filters?: NlpFilters): string[] {
  if (!filters) return []

  const badges: string[] = []

  switch (filters.stops) {
    case 'direct':
      badges.push('Vol direct')
      break
    case 'one_stop_max':
      badges.push('1 escale max')
      break
    case 'two_stops_max':
      badges.push('2 escales max')
      break
    default:
      break
  }

  if (typeof filters.maxPrice === 'number' && Number.isFinite(filters.maxPrice)) {
    badges.push(`Prix max: ${filters.maxPrice} EUR`)
  }

  if (filters.excludedAirlines && filters.excludedAirlines.length > 0) {
    const names = filters.excludedAirlines.map((code) => AIRLINE_NAMES[code] ?? code)
    badges.push(`Sans: ${names.join(', ')}`)
  }

  if (filters.luggage) {
    switch (filters.luggage) {
      case '0':
        badges.push('0 bagage et +')
        break
      case '1':
        badges.push('1 bagage min')
        break
      case '2':
        badges.push('2 bagages min')
        break
      case 'none':
        badges.push('Sans bagage')
        break
    }
  }

  if (
    typeof filters.maxDurationMinutes === 'number' &&
    Number.isFinite(filters.maxDurationMinutes)
  ) {
    const h = Math.floor(filters.maxDurationMinutes / 60)
    const m = filters.maxDurationMinutes % 60
    badges.push(`Durée max: ${h}h${m > 0 ? `${String(m).padStart(2, '0')}` : ''}`)
  }

  if (filters.timeFilters && filters.timeFilters.length > 0) {
    for (const tf of filters.timeFilters) {
      const label = `Vol ${tf.segmentIndex + 1}`
      const dep = tf.departureTimeSlots ?? []
      const arr = tf.arrivalTimeSlots ?? []
      if (dep.length > 0) {
        badges.push(`${label} départ: ${dep.map((s) => SLOT_LABELS[s] ?? s).join(', ')}`)
      }
      if (arr.length > 0) {
        badges.push(`${label} arrivée: ${arr.map((s) => SLOT_LABELS[s] ?? s).join(', ')}`)
      }
    }
  }

  return badges
}

function getSafeTransactionKey(
  offer: NlpFlightCardData,
  fallbackTransactionKey: string | undefined
): string {
  if (typeof offer.nlpTransactionKey === 'string' && offer.nlpTransactionKey.length > 0) {
    return offer.nlpTransactionKey
  }
  if (typeof fallbackTransactionKey === 'string' && fallbackTransactionKey.length > 0) {
    return fallbackTransactionKey
  }
  return ''
}

function renderPeriodSentence(params: null | StructuredMultiSearchRequest): ReactNode {
  if (!params || params.flights.length >= 3 || params.dateRanges.length === 0) {
    return <strong className="font-semibold text-gray-900">sur la période sélectionnée.</strong>
  }

  const outbound = params.dateRanges[0]
  const inbound = params.flights.length === 2 ? params.dateRanges[1] : undefined

  return (
    <>
      sur la période.{' '}
      <strong className="font-semibold text-gray-900">
        <br />
        Aller : du {formatDateNatural(outbound.from)} au {formatDateNatural(outbound.to)}
      </strong>
      {inbound && (
        <>
          {' / '}
          <strong className="font-semibold text-gray-900">
            Retour : du {formatDateNatural(inbound.from)} au {formatDateNatural(inbound.to)}
          </strong>
        </>
      )}
      .
    </>
  )
}
